import type { CSSProperties } from 'react';

export type IconName =
  | 'home'
  | 'timeline'
  | 'folder'
  | 'users'
  | 'mail'
  | 'euro'
  | 'decision'
  | 'camera'
  | 'check'
  | 'checkbox'
  | 'plus'
  | 'search'
  | 'bell'
  | 'settings'
  | 'filter'
  | 'chevR'
  | 'chevD'
  | 'chevL'
  | 'chevU'
  | 'more'
  | 'pdf'
  | 'image'
  | 'download'
  | 'upload'
  | 'paperclip'
  | 'edit'
  | 'trash'
  | 'star'
  | 'flag'
  | 'pin'
  | 'clipboard'
  | 'calendar'
  | 'clock'
  | 'phone'
  | 'map'
  | 'drag'
  | 'archive'
  | 'shield'
  | 'tool'
  | 'receipt'
  | 'arch'
  | 'eye'
  | 'link'
  | 'mic'
  | 'menu'
  | 'rotateL'
  | 'rotateR'
  | 'close';

interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
  'aria-label'?: string;
  'aria-hidden'?: boolean;
}

const PATHS: Record<IconName, React.ReactNode> = {
  home: <path d="M3 10l9-7 9 7v10a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1V10z" />,
  timeline: (
    <>
      <circle cx="6" cy="12" r="2" />
      <circle cx="18" cy="12" r="2" />
      <circle cx="12" cy="6" r="2" />
      <circle cx="12" cy="18" r="2" />
      <path d="M6 12h12M12 6v12" strokeDasharray="2 2" />
    </>
  ),
  folder: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />,
  users: (
    <>
      <circle cx="9" cy="9" r="3" />
      <path d="M3 19c0-3 3-5 6-5s6 2 6 5" />
      <circle cx="17" cy="8" r="2.4" />
      <path d="M15 14c4 0 6 2 6 4" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 7 9-7" />
    </>
  ),
  euro: <path d="M19 6a8 8 0 1 0 0 12M4 10h11M4 14h11" />,
  decision: <path d="M6 4l-3 4 3 4M18 4l3 4-3 4M9 8h6M12 12v8M9 20h6" />,
  camera: (
    <>
      <path d="M4 8a2 2 0 0 1 2-2h2l1.5-2h5L16 6h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  check: <path d="M4 12.5L9 17 20 6" />,
  checkbox: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M7 12l3 3 7-7" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  close: <path d="M6 6l12 12M18 6l-12 12" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M16 16l5 5" />
    </>
  ),
  bell: (
    <>
      <path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2H4.5L6 16z" />
      <path d="M10 21h4" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </>
  ),
  filter: <path d="M4 5h16M7 12h10M10 19h4" />,
  chevR: <path d="M9 6l6 6-6 6" />,
  chevD: <path d="M6 9l6 6 6-6" />,
  chevL: <path d="M15 6l-6 6 6 6" />,
  chevU: <path d="M6 15l6-6 6 6" />,
  more: (
    <>
      <circle cx="5" cy="12" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="19" cy="12" r="1.4" />
    </>
  ),
  pdf: (
    <>
      <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v5h5" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="10" r="2" />
      <path d="M3 17l5-5 4 4 3-3 6 6" />
    </>
  ),
  download: <path d="M12 4v12m0 0l-4-4m4 4l4-4M5 20h14" />,
  upload: <path d="M12 20V8m0 0l-4 4m4-4l4 4M5 4h14" />,
  paperclip: <path d="M16 8l-7 7a3 3 0 1 0 4 4l8-8a5 5 0 1 0-7-7L7 11" />,
  edit: <path d="M14 4l6 6L8 22H2v-6L14 4z" />,
  trash: <path d="M4 7h16M9 7V4h6v3M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />,
  star: <path d="M12 3l2.7 5.6 6 .9-4.3 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.3 9.5l6-.9L12 3z" />,
  flag: <path d="M5 21V4M5 4h12l-2 4 2 4H5" />,
  pin: <path d="M12 21v-6M8 9V4h8v5l3 6H5l3-6z" />,
  clipboard: (
    <>
      <rect x="6" y="4" width="12" height="17" rx="1.5" />
      <rect x="9" y="2" width="6" height="4" rx="1" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  phone: (
    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a1 1 0 0 1-1 1A16 16 0 0 1 4 5a1 1 0 0 1 1-1z" />
  ),
  map: (
    <>
      <path d="M3 7v13l6-3 6 3 6-3V4l-6 3-6-3-6 3z" />
      <path d="M9 4v13M15 7v13" />
    </>
  ),
  drag: (
    <>
      <circle cx="9" cy="6" r="1.2" />
      <circle cx="15" cy="6" r="1.2" />
      <circle cx="9" cy="12" r="1.2" />
      <circle cx="15" cy="12" r="1.2" />
      <circle cx="9" cy="18" r="1.2" />
      <circle cx="15" cy="18" r="1.2" />
    </>
  ),
  archive: (
    <>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4" />
    </>
  ),
  shield: <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" />,
  tool: (
    <path d="M14 6a4 4 0 0 1 4 4 4 4 0 0 1-1 2.7l5 5-3 3-5-5A4 4 0 0 1 6 6l3 3 2-2-3-3a4 4 0 0 1 6 2z" />
  ),
  receipt: (
    <>
      <path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2V3z" />
      <path d="M8 8h8M8 12h8M8 16h4" />
    </>
  ),
  arch: (
    <>
      <path d="M4 21V10a8 8 0 0 1 16 0v11" />
      <path d="M4 21h16" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  link: (
    <path d="M10 14a3.5 3.5 0 0 0 5 0l4-4a3.5 3.5 0 0 0-5-5l-2 2M14 10a3.5 3.5 0 0 0-5 0l-4 4a3.5 3.5 0 0 0 5 5l2-2" />
  ),
  mic: (
    <>
      <rect x="9" y="3" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3M9 21h6" />
    </>
  ),
  rotateL: (
    <>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
    </>
  ),
  rotateR: (
    <>
      <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
      <path d="M21 3v5h-5" />
    </>
  ),
};

export function Icon({
  name,
  size = 16,
  color = 'currentColor',
  strokeWidth = 1.6,
  className,
  style,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden = !ariaLabel,
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ flexShrink: 0, ...style }}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden || undefined}
    >
      {PATHS[name]}
    </svg>
  );
}
